import { useState, useEffect, useCallback, useMemo } from 'react';
import { Input, Select, message, Radio } from 'antd';
import { ArrowLeft } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import { projectApi, scheduleApi, brandApi, templateApi } from '../api/services';
import type { BrandVO, MilestoneTemplateVO, ProjectVO } from '../api/types';
import {
  IpcButton,
  IpcFilterToolbar,
  IpcModal,
  IpcStatusBadge,
  IpcTable,
  IPC_FILTER_SELECT_POPUP_PROPS,
} from './ui';
import './ImportConfigModal.css';

interface SourceProject {
  id: number;
  code: string;
  name: string;
  brand: string;
  templateId: number | null | undefined;
  scheduleCount: number;
}

interface SourceVersion {
  scheduleId: number;
  version: number;
  name: string;
  status: string;
  launchDate: string;
  specs: number;
  updated: string;
}

interface Props {
  open: boolean;
  currentProjectId: number;
  /** 分页每页条数，默认 20 */
  pageSize?: number;
  onClose: () => void;
  onImported: () => void;
}

function formatDateOnly(value: string) {
  if (!value) return '—';
  const datePart = value.length >= 10 ? value.slice(0, 10) : value;
  // 后端空日期常见占位
  if (datePart.startsWith('0001-01-01') || datePart.startsWith('1970-01-01')) return '—';
  return datePart;
}

function renderVersionStatus(status: string) {
  if (status === 'published') {
    return <IpcStatusBadge variant="success">生效中</IpcStatusBadge>;
  }
  if (status === 'draft') {
    return <IpcStatusBadge variant="warning">草稿</IpcStatusBadge>;
  }
  if (status === 'archived') {
    return <IpcStatusBadge variant="muted">已归档</IpcStatusBadge>;
  }
  return <IpcStatusBadge>{status}</IpcStatusBadge>;
}

const ImportConfigModal: React.FC<Props> = ({
  open,
  currentProjectId,
  pageSize: pageSizeProp,
  onClose,
  onImported,
}) => {
  const [allProjects, setAllProjects] = useState<SourceProject[]>([]);
  const [brands, setBrands] = useState<BrandVO[]>([]);
  const [templates, setTemplates] = useState<MilestoneTemplateVO[]>([]);
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState<'project' | 'version'>('project');
  const [selectedProject, setSelectedProject] = useState<SourceProject | null>(null);
  const [versions, setVersions] = useState<SourceVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [filterBrand, setFilterBrand] = useState<string>('');
  const [filterTemplate, setFilterTemplate] = useState<number | ''>('');
  const [appliedSearchText, setAppliedSearchText] = useState('');
  const [appliedFilterBrand, setAppliedFilterBrand] = useState<string>('');
  const [appliedFilterTemplate, setAppliedFilterTemplate] = useState<number | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeProp ?? 20);

  useEffect(() => {
    if (pageSizeProp != null) setPageSize(pageSizeProp);
  }, [pageSizeProp]);

  useEffect(() => {
    if (!open) return;
    brandApi.getList().then(list => setBrands(list ?? [])).catch(() => {});
    templateApi.getList({ pageNum: 1, pageSize: 999 }).then(res => setTemplates(res.list ?? [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    projectApi
      .getList({ pageNum: 1, pageSize: 999 })
      .then(res => {
        const list = (res.list ?? []) as ProjectVO[];
        const mapped: SourceProject[] = list
          .filter(p => p.id !== currentProjectId)
          .map(p => ({
            id: p.id,
            code: p.code ?? '',
            name: p.name ?? '',
            brand: p.brand ?? '',
            templateId: p.template_id,
            // 与项目列表页一致：直接用接口 schedule_count，避免 N+1 回填
            scheduleCount: p.schedule_count ?? 0,
          }));
        setAllProjects(mapped);
      })
      .catch(() => message.error('加载项目列表失败'))
      .finally(() => setLoading(false));
  }, [open, currentProjectId]);

  const filtered = allProjects.filter(p => {
    if (appliedSearchText) {
      const s = appliedSearchText.toLowerCase();
      if (!p.code.toLowerCase().includes(s) && !p.name.toLowerCase().includes(s)) return false;
    }
    if (appliedFilterBrand && p.brand !== appliedFilterBrand) return false;
    if (appliedFilterTemplate !== '' && p.templateId !== appliedFilterTemplate) return false;
    return true;
  });

  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getBrandDisplayName = useCallback(
    (brandCode: string) => brands.find(b => b.code === brandCode)?.name ?? brandCode,
    [brands],
  );

  const getTemplateDisplayName = useCallback((templateId: number | null | undefined) => {
    if (templateId == null || templateId <= 0) return '—';
    return templates.find(t => Number(t.id) === Number(templateId))?.name ?? `模板#${templateId}`;
  }, [templates]);

  const handleSelectProject = useCallback(async (project: SourceProject) => {
    setSelectedProject(project);
    try {
      const schedules = await scheduleApi.getListByProject(project.id);
      const sorted = [...schedules].sort((a, b) => b.version - a.version);
      const vs: SourceVersion[] = sorted.map(s => ({
        scheduleId: s.id,
        version: s.version,
        name: s.name,
        status: s.status,
        launchDate: s.expected_launch_date ?? '',
        specs: s.spec_count ?? 0,
        updated: s.update_time || s.create_time || '',
      }));
      setVersions(vs);
      setSelectedVersionId(vs.length > 0 ? vs[0].scheduleId : null);
      setStep('version');
    } catch {
      message.error('加载版本列表失败');
    }
  }, []);

  const handleBack = useCallback(() => {
    setStep('project');
    setSelectedProject(null);
    setVersions([]);
    setSelectedVersionId(null);
  }, []);

  const handleClose = useCallback(() => {
    setStep('project');
    setSelectedProject(null);
    setSelectedVersionId(null);
    setSearchText('');
    setFilterBrand('');
    setFilterTemplate('');
    setAppliedSearchText('');
    setAppliedFilterBrand('');
    setAppliedFilterTemplate('');
    setCurrentPage(1);
    onClose();
  }, [onClose]);

  const handleImport = useCallback(async () => {
    if (!selectedProject || !selectedVersionId) return;
    setImporting(true);
    try {
      await scheduleApi.create({
        project_id: currentProjectId,
        name: `导入自${selectedProject.name}`,
        expected_launch_date: new Date().toISOString().split('T')[0],
        source_schedule_id: selectedVersionId,
      });
      message.success('导入配置成功');
      onImported();
      handleClose();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '导入失败');
    } finally {
      setImporting(false);
    }
  }, [selectedProject, selectedVersionId, currentProjectId, onImported, handleClose]);

  const applyImportFilters = useCallback((next: {
    search: string;
    brand: string;
    template: string;
  }) => {
    if (
      next.search === appliedSearchText
      && next.brand === appliedFilterBrand
      && next.template === appliedFilterTemplate
    ) return;
    setAppliedSearchText(next.search);
    setAppliedFilterBrand(next.brand);
    setAppliedFilterTemplate(next.template);
    setCurrentPage(1);
  }, [appliedSearchText, appliedFilterBrand, appliedFilterTemplate]);

  const handleSearch = useCallback(() => {
    applyImportFilters({
      search: searchText.trim(),
      brand: filterBrand,
      template: filterTemplate,
    });
  }, [applyImportFilters, filterBrand, filterTemplate, searchText]);

  const clearAllImportFilters = useCallback(() => {
    setSearchText('');
    setFilterBrand('');
    setFilterTemplate('');
    setAppliedSearchText('');
    setAppliedFilterBrand('');
    setAppliedFilterTemplate('');
    setCurrentPage(1);
  }, []);
  const resetImportFiltersToDefault = clearAllImportFilters;

  const appliedFilterChips = useMemo(
    () =>
      [
        appliedSearchText
          ? { key: 'search', label: `项目名称/编号: ${appliedSearchText}` }
          : null,
        appliedFilterBrand
          ? { key: 'brand', label: `品牌: ${getBrandDisplayName(appliedFilterBrand)}` }
          : null,
        appliedFilterTemplate !== ''
          ? { key: 'template', label: `模板: ${getTemplateDisplayName(appliedFilterTemplate)}` }
          : null,
      ].filter((item): item is { key: string; label: string } => item != null),
    [
      appliedFilterBrand,
      appliedFilterTemplate,
      appliedSearchText,
      getBrandDisplayName,
      getTemplateDisplayName,
    ],
  );

  const removeAppliedFilterChip = useCallback((key: string) => {
    if (key === 'search') {
      setSearchText('');
      setAppliedSearchText('');
    } else if (key === 'brand') {
      setFilterBrand('');
      setAppliedFilterBrand('');
    } else if (key === 'template') {
      setFilterTemplate('');
      setAppliedFilterTemplate('');
    }
    setCurrentPage(1);
  }, []);

  const modalTitle = step === 'project' ? '导入配置 — 选择源项目' : '导入配置 — 选择版本';

  const versionBrandLabel = selectedProject
    ? [selectedProject.code, selectedProject.name].filter(Boolean).join(' ') || '选择版本'
    : '选择版本';

  const versionMetaLabel = selectedProject
    ? [getBrandDisplayName(selectedProject.brand), getTemplateDisplayName(selectedProject.templateId)]
        .filter(Boolean)
        .join(' · ')
    : '';

  const projectTableColumns = useMemo<ColumnsType<SourceProject>>(
    () => [
      {
        title: '项目',
        key: 'project',
        width: 260,
        render: (_, project) => {
          const projectLabel = [project.code, project.name].filter(Boolean).join(' ');
          return (
            <span className="import-config-modal__col-project" title={projectLabel}>
              {projectLabel || '—'}
            </span>
          );
        },
      },
      {
        title: '品牌',
        dataIndex: 'brand',
        width: 120,
        render: brand => {
          const brandName = getBrandDisplayName(brand);
          return (
            <span className="import-config-modal__col-brand" title={brandName}>
              {brandName || '—'}
            </span>
          );
        },
      },
      {
        title: '模板',
        dataIndex: 'templateId',
        width: 180,
        render: templateId => {
          const templateName = getTemplateDisplayName(templateId);
          return (
            <span className="import-config-modal__col-template" title={templateName}>
              {templateName}
            </span>
          );
        },
      },
      {
        title: '版本',
        dataIndex: 'scheduleCount',
        width: 92,
        align: 'right',
        render: scheduleCount => (
          <span
            className={[
              'import-config-modal__col-count',
              scheduleCount > 0 ? '' : 'import-config-modal__col-count--empty',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {scheduleCount > 0 ? `${scheduleCount} 版本` : '无排期'}
          </span>
        ),
      },
    ],
    [getBrandDisplayName, getTemplateDisplayName],
  );

  const versionTableColumns = useMemo<ColumnsType<SourceVersion>>(
    () => [
      {
        title: '',
        key: 'checked',
        width: 44,
        align: 'center',
        render: (_, version) => (
          <span className="import-config-modal__version-check" onClick={event => event.stopPropagation()}>
            <Radio
              checked={selectedVersionId === version.scheduleId}
              onChange={() => setSelectedVersionId(version.scheduleId)}
            />
          </span>
        ),
      },
      {
        title: '版本',
        dataIndex: 'version',
        width: 64,
        render: version => <span className="import-config-modal__version-no">V{version}</span>,
      },
      {
        title: '名称',
        dataIndex: 'name',
        width: 150,
        render: name => (
          <span className="import-config-modal__version-name" title={name}>
            {name || '—'}
          </span>
        ),
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 88,
        render: renderVersionStatus,
      },
      {
        title: '上市日期',
        dataIndex: 'launchDate',
        width: 112,
        render: formatDateOnly,
      },
      {
        title: '规格数',
        dataIndex: 'specs',
        width: 76,
        align: 'center',
        render: specs => <span className="import-config-modal__version-num">{specs}</span>,
      },
      {
        title: '更新时间',
        dataIndex: 'updated',
        width: 112,
        align: 'right',
        render: updated => (
          <span className="import-config-modal__version-num">{formatDateOnly(updated)}</span>
        ),
      },
    ],
    [selectedVersionId],
  );

  const modalFooter =
    step === 'project' ? (
      <div className="ipc-modal__footer-actions import-config-modal__footer">
        <IpcButton variant="link" onClick={handleClose}>
          取消
        </IpcButton>
      </div>
    ) : (
      <div className="ipc-modal__footer-actions import-config-modal__footer">
        <IpcButton variant="link" onClick={handleClose}>
          取消
        </IpcButton>
        <IpcButton
          variant="primary"
          onClick={() => void handleImport()}
          disabled={!selectedVersionId || importing}
          loading={importing}
        >
          导入
        </IpcButton>
      </div>
    );

  return (
    <IpcModal
      className="import-config-modal"
      title={<span className="import-config-modal__title-text">{modalTitle}</span>}
      open={open}
      onCancel={handleClose}
      width={900}
      centered
      footer={modalFooter}
    >
      <div className="import-config-modal__body ipc-table-viewport">
        {step === 'project' ? (
          <>
            <div className="ipc-table-viewport__top import-config-modal__top">
              <IpcFilterToolbar
                className="import-config-modal__filters"
                onSubmit={handleSearch}
                onReset={resetImportFiltersToDefault}
                chipItems={appliedFilterChips}
                onRemoveChip={removeAppliedFilterChip}
                onClearChips={clearAllImportFilters}
              >
                <Input
                  className="ipc-filter-bar__search import-config-modal__search"
                  placeholder="项目名称 / 编号搜索..."
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onPressEnter={handleSearch}
                  allowClear
                />
                <Select
                  className="ipc-filter-bar__field import-config-modal__select"
                  placeholder="全部品牌"
                  value={filterBrand || undefined}
                  onChange={v => {
                    const next = v ?? '';
                    setFilterBrand(next);
                    applyImportFilters({
                      search: searchText.trim(),
                      brand: next,
                      template: filterTemplate,
                    });
                  }}
                  allowClear
                  options={brands.map(b => ({ value: b.code, label: b.name }))}
                  {...IPC_FILTER_SELECT_POPUP_PROPS}
                />
                <Select
                  className="ipc-filter-bar__field import-config-modal__select"
                  placeholder="全部模板"
                  value={filterTemplate || undefined}
                  onChange={v => {
                    const next = v ?? '';
                    setFilterTemplate(next);
                    applyImportFilters({
                      search: searchText.trim(),
                      brand: filterBrand,
                      template: next,
                    });
                  }}
                  allowClear
                  options={templates.map(t => ({ value: Number(t.id), label: t.name ?? `模板#${t.id}` }))}
                  {...IPC_FILTER_SELECT_POPUP_PROPS}
                />
              </IpcFilterToolbar>
             
            </div>

            <div className="ipc-table-viewport__table">
              <IpcTable<SourceProject>
                compact
                resizable
                autoHeight
                autoHeightMinBodyHeight={200}
                autoHeightDeps={[paged.length, loading, step]}
                rowKey={project => project.id}
                columns={projectTableColumns}
                dataSource={paged}
                loading={loading}
                pagination={
                  !loading
                    ? {
                        className: 'import-config-modal__pagination',
                        current: currentPage,
                        total: filtered.length,
                        pageSize,
                        pageSizeOptions: pageSizeProp != null ? [pageSizeProp] : [10, 20, 50],
                        hideOnSinglePage: false,
                        onChange: (page, size) => {
                          if (pageSizeProp == null && size !== pageSize) {
                            setPageSize(size);
                            setCurrentPage(1);
                            return;
                          }
                          setCurrentPage(page);
                        },
                      }
                    : false
                }
                size="small"
                tableLayout="fixed"
                scroll={{ x: 652 }}
                panelClassName="import-config-modal__list-panel"
                scrollClassName="import-config-modal__list-wrap"
                className="import-config-modal__project-table"
                rowClassName={project =>
                  `import-config-modal__list-row ${project.scheduleCount > 0 ? 'is-clickable' : 'is-disabled'}`
                }
                onRow={project => {
                  const selectable = project.scheduleCount > 0;
                  return {
                    onClick: () => {
                      if (selectable) void handleSelectProject(project);
                    },
                    role: selectable ? 'button' : undefined,
                    tabIndex: selectable ? 0 : undefined,
                    onKeyDown: event => {
                      if (selectable && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        void handleSelectProject(project);
                      }
                    },
                  };
                }}
                emptyTitle="暂无符合条件的项目"
                emptyDescription=""
              />
            </div>
          </>
        ) : (
          <>
            <div className="import-config-modal__version-bar">
              <button type="button" className="import-config-modal__version-back" onClick={handleBack}>
                <ArrowLeft size={14} />
                返回列表
              </button>
              <div className="import-config-modal__version-context" title={[versionBrandLabel, versionMetaLabel].filter(Boolean).join(' · ')}>
                <span className="import-config-modal__version-brand">{versionBrandLabel}</span>
                {versionMetaLabel ? (
                  <span className="import-config-modal__version-meta"> · {versionMetaLabel}</span>
                ) : null}
              </div>
            </div>

            <div className="ipc-table-viewport__table">
              <IpcTable<SourceVersion>
                compact
                resizable
                autoHeight
                autoHeightMinBodyHeight={160}
                autoHeightDeps={[versions.length, step]}
                rowKey={version => version.scheduleId}
                columns={versionTableColumns}
                dataSource={versions}
                pagination={false}
                size="small"
                tableLayout="fixed"
                scroll={{ x: 746 }}
                panelClassName="import-config-modal__version-table-panel"
                scrollClassName="import-config-modal__version-table-wrap"
                className="import-config-modal__version-table"
                rowClassName={version =>
                  `import-config-modal__version-table-row ${
                    selectedVersionId === version.scheduleId ? 'is-selected' : ''
                  }`
                }
                onRow={version => ({ onClick: () => setSelectedVersionId(version.scheduleId) })}
                emptyTitle="暂无版本"
                emptyDescription=""
              />
            </div>
          </>
        )}
      </div>
    </IpcModal>
  );
};

export default ImportConfigModal;
